import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["badgeDescription", "badgeLabel", "children", "placement", "showBadge", "size"];
import { cloneElement, useEffect, useState } from 'react';
import styled, { css } from 'foundations-theming/utils';
import { uniqueId } from '../../utils/uniqueId';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { Badge, StyledBadge as StyledBadgeWrapper } from '../Badge/Badge';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { TwoWayTransition } from '../TwoWayTransition/TwoWayTransition';
import { keyframes } from 'styled-components';
import { getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getTransitionDuration = (firstRender, open) => {
  if (firstRender && open) return 150;
  return open ? 300 : 200;
};
const BADGE_SIZE = {
  default: 'count',
  md: 'count',
  sm: 'dot'
};
const popIn = keyframes(["0%{animation-timing-function:cubic-bezier(.16,.84,.44,1);transform:scale(1);}50%{animation-timing-function:cubic-bezier(.89,.03,.68,.22);transform:scale(1.25);}100%{transform:scale(1);}"]);
const scaleIn = keyframes(["0%{transform:scale(.75);}100%{transform:scale(1);}"]);
const fadeIn = keyframes(["0%{opacity:0;}33%{opacity:1;}"]);
const scaleOut = keyframes(["0%{transform:scale(1);}75%{transform:scale(.75);}"]);
const fadeOut = keyframes(["0%,25%{opacity:1;}100%{opacity:0;}"]);
const NotificationBadgeTransitionWrapper = styled.div.withConfig({
  displayName: "NotificationBadge__NotificationBadgeTransitionWrapper"
})([".hs-private.hs-private.hs-private & ", " > *:first-child{", ";opacity:", ";}"], StyledBadgeWrapper, ({
  firstRender,
  open,
  transitionActive
}) => {
  if (!transitionActive) return '';
  if (firstRender && open) return css(["animation:", " 150ms;"], popIn);
  return open ? css(["animation:", " 300ms cubic-bezier(0.2,0.9,0.3,2),", " 150ms cubic-bezier(0.42,0,0.58,1);"], scaleIn, fadeIn) : css(["animation:", " 150ms cubic-bezier(0.89,0.03,0.68,0.22),", " 200ms cubic-bezier(0.42,0,0.58,1);"], scaleOut, fadeOut);
}, ({
  open
}) => open ? 1 : 0);
const Wrapper = styled.div.withConfig({
  displayName: "NotificationBadge__Wrapper"
})([".hs-private.hs-private.hs-private &{position:relative;display:inline-flex;align-items:baseline;line-height:1;", ";}"], ({
  $cssStyles
}) => $cssStyles);
const insetBadgeMixin = css(["position:absolute !important;top:0;right:5px;transform:translate(25%,-25%);"]);
const StyledBadge = styled.div.withConfig({
  displayName: "NotificationBadge__StyledBadge"
})([".hs-private.hs-private.hs-private &{", " ", " ", "}"], ({
  placement
}) => placement === 'inset' && insetBadgeMixin, ({
  placement,
  use
}) => placement === 'outset' && use === 'dot' && css(["position:absolute !important;top:0;right:0;transform:translate(40%,-40%);z-index:1;"]), ({
  placement,
  use
}) => placement === 'outset' && use !== 'dot' && css(["position:absolute !important;top:0;right:0;transform:translate(40%,-40%);z-index:1;"]));
const StyledBadgeDescription = styled.span.withConfig({
  displayName: "NotificationBadge__StyledBadgeDescription"
})(_ref => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  const theme = rest.theme;
  return Object.assign({}, getScreenReaderOnlyStyles(theme));
});
export const NotificationBadge = _ref2 => {
  let {
      badgeDescription,
      badgeLabel,
      children,
      placement = 'outset',
      showBadge = true,
      size = 'md'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const [hasRendered, setHasRendered] = useState(false);
  const descriptionId = uniqueId('notification-badge-');
  useEffect(() => {
    setHasRendered(true);
  }, []);
  const firstRender = !hasRendered;
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const shorthandSize = toShorthandSize(size);
  let useType = BADGE_SIZE[shorthandSize];
  if (badgeLabel && String(badgeLabel).trim()) {
    useType = 'count';
  }
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, domProps, {
    $cssStyles: cssStyles,
    children: [/*#__PURE__*/cloneElement(children, {
      'aria-describedby': showBadge ? descriptionId : undefined
    }), /*#__PURE__*/_jsx(TwoWayTransition, {
      duration: getTransitionDuration(firstRender, showBadge),
      firstRender: firstRender,
      transitionOnMount: true,
      Wrapper: NotificationBadgeTransitionWrapper,
      children: showBadge && /*#__PURE__*/_jsx(StyledBadge, {
        placement: placement,
        use: useType,
        children: /*#__PURE__*/_jsxs(Badge, {
          id: descriptionId,
          use: useType,
          children: [badgeLabel, /*#__PURE__*/_jsx(StyledBadgeDescription, {
            children: badgeDescription
          })]
        })
      })
    })]
  }));
};
NotificationBadge.displayName = 'NotificationBadge';